import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TransactionModel, TransactionWrapper } from '@core/models';
import { ApiException } from '@core/services/base-connection.service';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ConnectionService } from '../../services/connection.service';

@Component({
  selector: 'app-discount-code',
  imports: [FormsModule, TooltipModule],
  templateUrl: './discount-code.component.html',
  styleUrl: './discount-code.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscountCodeComponent {
  private connectionService = inject(ConnectionService);

  transaction = input.required<TransactionWrapper>();
  // True while the currently selected upsell's buyer type isn't editable in ProVenue: applying
  // a discount code re-prices every ticket in the seats offer via editTicketPrice, which
  // ProVenue rejects outright if any of those tickets sit under a non-editable buyer type. Set
  // by the parent from the mirror condition to GroupOutingComponent.isUpsellDisabled.
  disabled = input<boolean>(false);
  transactionChange = output<TransactionWrapper>();

  code = signal('');
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  private lastTransactionId: number | string | null = null;

  constructor() {
    // This component instance can outlive a single transaction (a cancel/restart/
    // expiry flow deletes the current transaction and creates a new one without necessarily
    // destroying this component). Without this, a stale typed code or a leftover error message
    // from the old transaction would keep showing on the new one.
    effect(() => {
      const currentTransactionId = this.transaction()?.transaction?.id ?? null;
      if (currentTransactionId !== this.lastTransactionId) {
        this.lastTransactionId = currentTransactionId;
        this.code.set('');
        this.errorMessage.set(null);
      }
    });
  }

  get hasDiscount(): boolean {
    return !!this.transaction()?.transaction?.discountCode;
  }

  get appliedCode(): string | null {
    return this.transaction()?.transaction?.discountCode ?? null;
  }

  apply() {
    if (this.disabled()) {
      return;
    }

    const code = this.code().trim();
    if (!code) {
      return;
    }

    const transaction = this.transaction();
    if (!transaction) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const transactionId = transaction.transaction.id.toString();

    this.connectionService.applyDiscountCode(transactionId, code).subscribe({
      next: result => {
        this.isLoading.set(false);
        this.code.set('');

        if (result.data) {
          const updatedTransaction: TransactionWrapper = {
            ...transaction,
            transaction: result.data as TransactionModel,
          };
          this.transactionChange.emit(updatedTransaction);
        }
      },
      error: error => {
        this.isLoading.set(false);

        if (error instanceof ApiException) {
          this.errorMessage.set(error.message);
        } else {
          this.errorMessage.set(
            'An error occurred while applying the discount code.'
          );
        }
      },
    });
  }

  remove() {
    const transaction = this.transaction();
    if (!transaction) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const transactionId = transaction.transaction.id.toString();

    this.connectionService.removeDiscountCode(transactionId).subscribe({
      next: result => {
        this.isLoading.set(false);

        if (result.data) {
          const updatedTransaction: TransactionWrapper = {
            ...transaction,
            transaction: result.data as TransactionModel,
          };
          this.transactionChange.emit(updatedTransaction);
        }
      },
      error: error => {
        this.isLoading.set(false);

        if (error instanceof ApiException) {
          this.errorMessage.set(error.message);
        } else {
          this.errorMessage.set(
            'An error occurred while removing the discount code.'
          );
        }
      },
    });
  }
}
